import * as TabsPrimitive from "@radix-ui/react-tabs";
import { ArrowRight, ChevronLeft, ChevronRight, Clock } from "lucide-react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { cn } from "../../../lib/utils";
import {
  READINESS_FACTOR_LABELS,
  buildFactorBreakdown,
  parseHorizonMonths,
  type FactorBreakdown,
} from "../../../lib/readiness/breakdown";
import type {
  ReadinessGoal,
  ReadinessPlanViability,
  ReadinessResult,
  ReadinessTimeUnlock,
} from "../../../lib/readiness-types";


// Label rendered above the grouped supercell that combines down_payment
// and cash_execution into a single visual row. See the 2026-05-25 spec
// §6.5b — keeps the 4×2 grid intact while telling the unified cash story.
const CASH_GROUP_LABEL = "Cash position";

type TabValue = "boost" | "factors" | "timeline";

export interface OverviewBreakdownTabsProps {
  readiness: ReadinessResult;
}

export function OverviewBreakdownTabs({
  readiness,
}: OverviewBreakdownTabsProps) {
  // Backend enforces the goal cap (3 non-lever + optional paired lever
  // per SILL-323 capGoalsPreservingLever). Trust it — slicing here
  // would silently drop the income lever from the carousel.
  const goals = readiness.goals ?? [];
  // SILL-323: when the headline is capped (score === null), show
  // applies=false factors as em-dash "Not yet provided" rows so the
  // breakdown matches the missing-headline story. With a scored
  // headline, drop them as before — they'd contradict the number.
  const showUnknownFactors = readiness.score === null;
  const factors = buildFactorBreakdown(readiness, showUnknownFactors);
  const unlocks = readiness.time_unlocks ?? [];

  // 2026-05-25 Fix 3: lever goals carry the same projected_delta as their
  // companion raise goal (DTI math is symmetric — paying down debt and
  // lifting income cross the same bucket). Including both in the sum
  // would double-count. Coaching goals have projected_delta:0 so they're
  // already no-ops in the sum, but filter them explicitly for parity.
  const boostMeta = goals.length
    ? `${goals.length} ${goals.length === 1 ? "move" : "moves"} · up to +${goals.reduce(
        (sum, g) =>
          g.kind === "lever" || g.kind === "coaching"
            ? sum
            : sum + g.projected_delta,
        0,
      )} pts`
    : null;
  const factorsMeta = factors.length
    ? `${factors.length} ${factors.length === 1 ? "factor" : "factors"}`
    : null;
  const timelineMeta = buildTimelineMeta(unlocks);

  const triggers: Array<{ value: TabValue; label: string; meta: string | null }> = [
    { value: "boost", label: "Boost score", meta: boostMeta },
    { value: "factors", label: "Factors", meta: factorsMeta },
    { value: "timeline", label: "Timeline", meta: timelineMeta },
  ];

  return (
    <TabsPrimitive.Root
      defaultValue="boost"
      data-testid="readiness-breakdown-tabs"
      className="mt-5 flex flex-col gap-4"
    >
      <TabsPrimitive.List
        data-testid="readiness-tabs-list"
        className="-mx-6 flex items-stretch bg-black/25"
      >
        {triggers.map((tab) => (
          <TabsPrimitive.Trigger
            key={tab.value}
            value={tab.value}
            data-slot="interactive-control"
            className={cn(
              "group relative flex min-h-12 shrink-0 items-center justify-start gap-2 border-b-2 border-transparent px-3.5 pb-2.5 pt-3 text-left md:px-5",
              "text-panel-ink-foreground/45 transition-colors",
              "hover:text-panel-ink-foreground/75",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-panel-ink",
              "data-[state=active]:border-primary data-[state=active]:bg-panel-ink-foreground/[0.06] data-[state=active]:text-panel-ink-foreground",
            )}
          >
            <span
              data-tab-label
              className="text-sm font-semibold tracking-tight"
            >
              {tab.label}
            </span>
            {tab.meta ? (
              <span
                data-tab-meta
                className="hidden text-xs font-medium text-panel-ink-foreground/40 group-data-[state=active]:text-panel-ink-foreground/55 md:inline"
              >
                {tab.meta}
              </span>
            ) : null}
          </TabsPrimitive.Trigger>
        ))}
      </TabsPrimitive.List>

      <div
        data-testid="readiness-tabs-slot"
        className="relative"
      >
        <TabsPrimitive.Content value="boost" className="outline-none">
          {goals.length > 0 ? (
            <BoostGoals goals={goals} />
          ) : (
            <BreakdownEmptyState message="No boost actions yet — your profile is on track." />
          )}
        </TabsPrimitive.Content>

        <TabsPrimitive.Content value="factors" className="outline-none">
          {factors.length > 0 ? (
            <FactorGrid
              factors={factors}
              planViability={readiness.plan_viability}
            />
          ) : (
            <BreakdownEmptyState message="No applicable factors yet." />
          )}
        </TabsPrimitive.Content>

        <TabsPrimitive.Content value="timeline" className="outline-none">
          {unlocks.length > 0 ? (
            <TimelineList unlocks={unlocks} />
          ) : (
            <BreakdownEmptyState message="No time-based unlocks yet." />
          )}
        </TabsPrimitive.Content>
      </div>
    </TabsPrimitive.Root>
  );
}

function BreakdownEmptyState({ message }: { message: string }) {
  return (
    <p className="px-1 py-6 text-sm text-panel-ink-foreground/65">{message}</p>
  );
}

// -------- Boost score tab --------

const MOVE_EYEBROWS = ["TOP MOVE", "MOVE 2", "MOVE 3"] as const;

function BoostGoals({ goals }: { goals: ReadinessGoal[] }) {
  if (goals.length <= 3) {
    return (
      <section
        data-testid="readiness-goals"
        className="grid grid-cols-1 gap-3 md:grid-cols-3"
      >
        {goals.map((goal, idx) => (
          <GoalCard
            key={`${goal.factor}-${goal.action_label}`}
            goal={goal}
            eyebrow={MOVE_EYEBROWS[idx] ?? `MOVE ${idx + 1}`}
          />
        ))}
      </section>
    );
  }
  return <BoostGoalCarousel goals={goals} />;
}

function BoostGoalCarousel({ goals }: { goals: ReadinessGoal[] }) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);

  const updateButtons = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    updateButtons();
    el.addEventListener("scroll", updateButtons, { passive: true });
    window.addEventListener("resize", updateButtons);
    return () => {
      el.removeEventListener("scroll", updateButtons);
      window.removeEventListener("resize", updateButtons);
    };
  }, [updateButtons]);

  const scroll = (dir: "prev" | "next") => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.firstElementChild as HTMLElement | null;
    const step = card ? card.offsetWidth + 12 : el.clientWidth * 0.9;
    el.scrollBy({
      left: dir === "next" ? step : -step,
      behavior: "smooth",
    });
  };

  // Mobile users swipe; chevrons only appear at md+ where pointer is
  // the primary input. Default look is a bare icon (less "blocky"
  // surface area); the bg + border lift on hover so it still reads as
  // a button under cursor.
  const arrowClass =
    "hidden md:inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-transparent bg-transparent text-panel-ink-foreground/65 transition hover:border-panel-ink-foreground/10 hover:bg-panel-ink-foreground/[0.08] hover:text-panel-ink-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-panel-ink-foreground/30 disabled:opacity-25 disabled:hover:border-transparent disabled:hover:bg-transparent";

  return (
    <section
      data-testid="readiness-goals"
      className="flex items-center gap-0 md:-mx-4 md:gap-1"
    >
      <button
        type="button"
        onClick={() => scroll("prev")}
        aria-label="Previous move"
        data-testid="boost-carousel-prev"
        disabled={!canPrev}
        className={arrowClass}
      >
        <ChevronLeft className="h-4 w-4" aria-hidden="true" />
      </button>
      <div
        ref={trackRef}
        data-testid="readiness-goals-carousel"
        className="flex min-w-0 flex-1 gap-3 overflow-x-auto scroll-smooth snap-x snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {goals.map((goal, idx) => (
          <div
            key={`${goal.factor}-${goal.action_label}`}
            className="w-[85%] shrink-0 snap-start md:w-[calc((100%-1.5rem)/3)]"
          >
            <GoalCard
              goal={goal}
              eyebrow={MOVE_EYEBROWS[idx] ?? `MOVE ${idx + 1}`}
            />
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={() => scroll("next")}
        aria-label="Next move"
        data-testid="boost-carousel-next"
        disabled={!canNext}
        className={arrowClass}
      >
        <ChevronRight className="h-4 w-4" aria-hidden="true" />
      </button>
    </section>
  );
}

// Three shapes for the Boost-card footer, picked by goal type. Keeping
// these explicit (rather than one parser-driven bar) avoids the
// regex-coincidence problem the old parseGoalProgress had — credit
// strings like "Below 660" → "660-699" both contain "660" and would
// silently render as 100% progress. Each shape is also visually distinct
// from the Factors-tab bar so users don't conflate "this factor's score
// share" with "progress toward this specific goal."
type GoalShape = "ladder" | "continuous" | "state" | "lever";

function shapeForGoal(goal: ReadinessGoal): GoalShape {
  // SILL-323: informational lever (e.g. "Lift income to lower DTI") has
  // no current/target and no projected delta — title + why is the whole
  // card. Take precedence over the factor-based branches below.
  if (goal.kind === "lever") return "lever";
  if (goal.kind === "set_plan") return "state";
  if (goal.factor === "credit") return "ladder";
  if (
    goal.factor === "down_payment" ||
    goal.factor === "profile_completeness"
  ) {
    return "continuous";
  }
  // DTI is a lower-is-better threshold (e.g. 42% → <43%) — a fill bar
  // implies a higher-is-better metric and would mislead. Income-absence
  // DNQ goals land here too with "Not recorded" → "Entered". State
  // chips communicate both honestly.
  return "state";
}

// NON_POINTS_GOAL_KINDS gates the "+N pts" badge. set_plan / lever /
// coaching all carry ProjectedDelta: 0 by contract — the badge would be
// a false promise (lever and coaching live on factors that may already
// be at max; set_plan is a state-change prompt with no points). Append
// to this set whenever a new GoalKind ships with ProjectedDelta: 0.
const NON_POINTS_GOAL_KINDS: ReadonlySet<ReadinessGoal["kind"]> = new Set([
  "set_plan",
  "lever",
  "coaching",
]);

function GoalCard({ goal, eyebrow }: { goal: ReadinessGoal; eyebrow: string }) {
  // SILL-323 follow-up: two kinds of cap-aware framing replace the
  // default "+N pts" badge:
  //   1. unlocks_segment present → the cap-resolving goal. Promise is
  //      structural ("Unlocks scoring"), not a points delta — the
  //      headline is null until the cap clears.
  //   2. kind ∈ NON_POINTS_GOAL_KINDS → no badge (see set declaration).
  // unlocks_segment wins precedence because the user is already in a
  // capped state when both apply.
  const isNonPointsGoal = NON_POINTS_GOAL_KINDS.has(goal.kind);
  const unlocksSegment = goal.unlocks_segment;
  const shape = shapeForGoal(goal);

  return (
    <article
      data-testid="boost-goal-card"
      data-goal-kind={goal.kind ?? "raise"}
      data-goal-shape={shape}
      className="flex h-full min-w-0 flex-col rounded-2xl border border-panel-ink-foreground/12 bg-panel-ink-foreground/[0.04] p-5"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <p
            data-card-eyebrow
            className="shrink-0 text-[11px] font-semibold tracking-[0.16em] text-panel-ink-foreground/55"
          >
            {eyebrow}
          </p>
          <span
            aria-hidden="true"
            className="shrink-0 text-[11px] font-semibold text-panel-ink-foreground/40"
          >
            ·
          </span>
          <span
            data-card-factor={goal.factor}
            className="truncate text-[11px] font-medium text-panel-ink-foreground/70"
          >
            {READINESS_FACTOR_LABELS[goal.factor]}
          </span>
        </div>
        {unlocksSegment ? (
          <span
            data-goal-badge="unlocks"
            className="shrink-0 rounded-full bg-panel-ink-foreground px-2.5 py-1 text-xs font-bold text-panel-ink"
          >
            Unlocks scoring
          </span>
        ) : isNonPointsGoal ? null : (
          <span
            data-goal-badge="points"
            className="shrink-0 rounded-full bg-panel-ink-foreground px-2.5 py-1 text-xs font-bold tabular-nums text-panel-ink"
          >
            + {goal.projected_delta} pts
          </span>
        )}
      </div>

      <h3 className="mt-3 text-[1.05rem] font-semibold leading-snug text-panel-ink-foreground">
        {goal.action_label}
      </h3>
      <p className="mt-2 flex-1 text-sm leading-6 text-panel-ink-foreground/70">
        {goal.why}
      </p>

      {shape === "lever" ? (
        <span data-goal-progress="lever" className="sr-only" />
      ) : (
        <div className="mt-4" data-goal-progress={shape}>
          {shape === "ladder" ? (
            <TierLadder goal={goal} />
          ) : shape === "continuous" ? (
            <ContinuousProgress goal={goal} />
          ) : (
            <StateChips goal={goal} />
          )}
        </div>
      )}
    </article>
  );
}

// ─── Goal shape: ladder (credit tier jumps) ────────────────────────────
//
// Four canonical credit tiers. Tier-jump goals show all four as
// discrete dots so the user can see WHICH tier they're in and how
// many tiers remain. The visual is intentionally NOT a fill bar — a
// 25% / 50% / 75% fill would imply linear progress between tiers, but
// credit-band promotions are discrete (a 660 score and a 698 score
// are in the same tier).

const CREDIT_TIERS = ["Below 660", "660-699", "700-739", "740+"] as const;

function TierLadder({ goal }: { goal: ReadinessGoal }) {
  const currentIdx = CREDIT_TIERS.indexOf(
    goal.current_state as (typeof CREDIT_TIERS)[number],
  );
  const targetIdx = CREDIT_TIERS.indexOf(
    goal.target_state as (typeof CREDIT_TIERS)[number],
  );

  return (
    <div data-shape="ladder">
      <div
        role="progressbar"
        aria-label={`${goal.action_label} tier ladder`}
        aria-valuemin={0}
        aria-valuemax={CREDIT_TIERS.length - 1}
        aria-valuenow={currentIdx >= 0 ? currentIdx : 0}
        aria-valuetext={`${goal.current_state} of ${goal.target_state}`}
        className="flex items-center justify-between gap-2"
      >
        {CREDIT_TIERS.map((tier, i) => {
          const isCurrent = i === currentIdx;
          const isTarget = i === targetIdx;
          const isPast = currentIdx >= 0 && i < currentIdx;
          return (
            <div
              key={tier}
              data-tier={tier}
              data-tier-state={
                isCurrent
                  ? "current"
                  : isTarget
                    ? "target"
                    : isPast
                      ? "past"
                      : "future"
              }
              className="flex min-w-0 flex-1 flex-col items-center gap-1.5"
            >
              <span
                aria-hidden="true"
                className={cn(
                  "h-2.5 w-full rounded-full",
                  isPast || isCurrent
                    ? "bg-primary"
                    : isTarget
                      ? // primary-soft is the lighter green token —
                        // bumps the ring contrast against the dark
                        // hero panel without losing the "green target"
                        // semantic.
                        "border-2 border-primary-soft bg-transparent"
                      : "bg-panel-ink-foreground/15",
                )}
              />
              <span
                className={cn(
                  "truncate text-[10.5px] font-medium leading-3",
                  isCurrent
                    ? "text-panel-ink-foreground"
                    : isTarget
                      ? "text-primary-soft"
                      : "text-panel-ink-foreground/55",
                )}
              >
                {tier}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Goal shape: continuous (savings $→$, completeness %→%) ────────────
//
// For goals where current/target are honestly numeric. The parser
// requires the SAME unit on both sides — `$X`/`$Y` or `X%`/`Y%`. Mixed
// or non-numeric content returns null and we fall back to the slim bar
// with labeled endpoints but no fill (no false-precision percent).

type ContinuousReading = {
  current: number;
  target: number;
  unit: "$" | "%";
} | null;

function readContinuous(current: string, target: string): ContinuousReading {
  const cDollar = current.match(/^\$([\d,]+(?:\.\d+)?)/);
  const tDollar = target.match(/^\$([\d,]+(?:\.\d+)?)/);
  if (cDollar && tDollar) {
    const c = Number(cDollar[1]!.replace(/,/g, ""));
    const t = Number(tDollar[1]!.replace(/,/g, ""));
    if (Number.isFinite(c) && Number.isFinite(t) && t > 0) {
      return { current: c, target: t, unit: "$" };
    }
  }
  // %-leading on both sides (e.g. "92% complete" → "100%").
  const cPct = current.match(/^(\d+(?:\.\d+)?)%/);
  const tPct = target.match(/^(\d+(?:\.\d+)?)%/);
  if (cPct && tPct) {
    const c = Number(cPct[1]);
    const t = Number(tPct[1]);
    if (Number.isFinite(c) && Number.isFinite(t) && t > 0) {
      return { current: c, target: t, unit: "%" };
    }
  }
  return null;
}

function ContinuousProgress({ goal }: { goal: ReadinessGoal }) {
  const reading = readContinuous(goal.current_state, goal.target_state);
  const ratio = reading ? clamp(reading.current / reading.target, 0, 1) : null;
  const percent = ratio !== null ? Math.round(ratio * 100) : null;
  const fillClass = goalBarTone(percent);

  return (
    <div data-shape="continuous">
      <div className="flex items-baseline justify-between gap-3 text-xs font-medium">
        <span
          className="truncate text-panel-ink-foreground"
          title={goal.current_state}
        >
          {goal.current_state}
        </span>
        <span
          className="truncate text-right text-panel-ink-foreground/60"
          title={goal.target_state}
        >
          {goal.target_state}
        </span>
      </div>
      <div
        className="mt-2 h-1.5 overflow-hidden rounded-full bg-panel-ink-foreground/14"
        role="progressbar"
        aria-label={`${goal.action_label} progress`}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent ?? 0}
        aria-valuetext={
          percent !== null
            ? `${percent}% — ${goal.current_state} of ${goal.target_state}`
            : `${goal.current_state} of ${goal.target_state}`
        }
      >
        {percent !== null ? (
          <span
            data-readiness-bar-fill=""
            className={cn("block h-full rounded-full", fillClass)}
            style={{ width: `${percent}%` }}
          />
        ) : null}
      </div>
    </div>
  );
}

// ─── Goal shape: state (DTI thresholds, set_plan, income absence) ──────
//
// Two pill chips with an arrow between — no bar. Honest for goals where
// progress isn't a meaningful continuous quantity:
//   - DTI: lower-is-better threshold (42% → <43%) — a fill direction
//     would lie
//   - set_plan: "$X saved (Y% of target)" → "Plan chosen" — destination
//     is a state, not a value
//   - income absence DNQ: "Not recorded" → "Entered" — binary

function StateChips({ goal }: { goal: ReadinessGoal }) {
  return (
    <div
      data-shape="state"
      className="flex flex-wrap items-center gap-2 text-xs font-medium"
    >
      <span
        data-state-chip="current"
        className="rounded-full bg-panel-ink-foreground/10 px-2.5 py-1 text-panel-ink-foreground"
        title={goal.current_state}
      >
        {goal.current_state}
      </span>
      <ArrowRight
        aria-hidden="true"
        className="h-3.5 w-3.5 shrink-0 text-panel-ink-foreground/55"
      />
      <span
        data-state-chip="target"
        // Keep the green outline + tint for the "target" affordance, but
        // raise the text to primary-soft so it stays legible on the dark
        // hero panel (saturated text-primary at L≈0.48 didn't contrast
        // well against bg-panel-ink at L≈0.22).
        className="rounded-full border border-primary-soft/50 bg-primary/10 px-2.5 py-1 text-primary-soft"
        title={goal.target_state}
      >
        {goal.target_state}
      </span>
    </div>
  );
}

// -------- Factors tab --------


type FactorGridEntry =
  | { kind: "single"; factor: FactorBreakdown }
  | {
      kind: "cash_group";
      // sortRatio drives placement in the grid (lowest of the two
      // sub-ratios so the group floats near the more-urgent member).
      sortRatio: number;
      downPayment: FactorBreakdown | null;
      cashExecution: FactorBreakdown | null;
    };

// groupFactorRows folds the down_payment + cash_execution pair into a
// single "Cash position" supercell entry — see the 2026-05-25 spec
// §6.5b. With cash_execution present the dashboard renders 8 visual
// cells instead of 9; without it, down_payment falls back to a single
// row and the grid stays at 8 cells.
function groupFactorRows(factors: FactorBreakdown[]): FactorGridEntry[] {
  const dp = factors.find((f) => f.id === "down_payment");
  const ce = factors.find((f) => f.id === "cash_execution");
  const others = factors.filter(
    (f) => f.id !== "down_payment" && f.id !== "cash_execution",
  );

  // No cash factors at all (e.g., DNQ profile with no target): emit only
  // the other singletons.
  if (!dp && !ce) {
    return others.map((factor) => ({ kind: "single", factor }) as const);
  }

  // The supercell sorts by the lower of the two sub-ratios so the
  // group rises in priority when EITHER sub-factor is the user's
  // weakest signal. When one is unknown (DNQ surface), use the known
  // one's ratio; when both unknown, fall back to 0 so the group sits
  // with the unknowns at the bottom of the sort.
  const ratios: number[] = [];
  if (dp && !dp.isUnknown) ratios.push(dp.ratio);
  if (ce && !ce.isUnknown) ratios.push(ce.ratio);
  const sortRatio = ratios.length > 0 ? Math.min(...ratios) : 0;

  const entries: FactorGridEntry[] = others.map(
    (factor) => ({ kind: "single", factor }) as const,
  );
  entries.push({
    kind: "cash_group",
    sortRatio,
    downPayment: dp ?? null,
    cashExecution: ce ?? null,
  });

  return entries.sort((a, b) => {
    const aRatio = a.kind === "single" ? a.factor.ratio : a.sortRatio;
    const bRatio = b.kind === "single" ? b.factor.ratio : b.sortRatio;
    const aUnknown =
      a.kind === "single"
        ? a.factor.isUnknown
        : (a.downPayment?.isUnknown ?? true) &&
          (a.cashExecution?.isUnknown ?? true);
    const bUnknown =
      b.kind === "single"
        ? b.factor.isUnknown
        : (b.downPayment?.isUnknown ?? true) &&
          (b.cashExecution?.isUnknown ?? true);
    if (aUnknown !== bUnknown) return aUnknown ? 1 : -1;
    return aRatio - bRatio;
  });
}

function FactorGrid({
  factors,
  planViability,
}: {
  factors: FactorBreakdown[];
  planViability?: ReadinessPlanViability;
}) {
  const entries = groupFactorRows(factors);
  return (
    <section
      data-testid="readiness-factors"
      className="grid grid-cols-1 gap-x-10 md:grid-cols-2"
    >
      {entries.map((entry) =>
        entry.kind === "single" ? (
          <FactorRow key={entry.factor.id} factor={entry.factor} />
        ) : (
          <FactorGroupRow
            key="cash_group"
            downPayment={entry.downPayment}
            cashExecution={entry.cashExecution}
            planViability={planViability}
          />
        ),
      )}
    </section>
  );
}

// FactorGroupRow renders the "Cash position" supercell — down_payment
// and cash_execution as two sub-bars in one grid cell. Sub-bars reuse
// the same label/bar/percent template as the singleton FactorRow at a
// reduced scale. When cash_execution.applies is false (cashtoclose
// adapter unwired), the supercell degrades to single-bar mode showing
// only down_payment with a "Plan-funding signal coming online"
// sublabel — same intent as the SILL-323 em-dash for unknown factors
// but tuned for the grouped case.
function FactorGroupRow({
  downPayment,
  cashExecution,
  planViability,
}: {
  downPayment: FactorBreakdown | null;
  cashExecution: FactorBreakdown | null;
  planViability?: ReadinessPlanViability;
}) {
  // Defensive: at least one sub-factor must be present to render the
  // group. Caller (groupFactorRows) won't emit a cash_group entry
  // otherwise, but this guard keeps the type narrowing honest.
  if (!downPayment && !cashExecution) return null;

  // Aggregate the supercell's headline using combined-points so the
  // "Cash position" bar follows the same "this is the contribution of
  // this factor to the score" contract as every other singleton row.
  // Sub-factors marked isUnknown contribute neither value nor max — they
  // shouldn't pad the denominator (would make the headline misleadingly
  // low) and shouldn't add to the numerator (no signal).
  const applyingSubs: FactorBreakdown[] = [];
  if (downPayment && !downPayment.isUnknown) applyingSubs.push(downPayment);
  if (cashExecution && !cashExecution.isUnknown)
    applyingSubs.push(cashExecution);

  const aggValue = applyingSubs.reduce((sum, f) => sum + f.value, 0);
  const aggMax = applyingSubs.reduce((sum, f) => sum + f.max, 0);
  const aggPercent =
    aggMax > 0 ? clamp(Math.round((aggValue / aggMax) * 100), 0, 100) : 0;

  // Confidence: both cash factors share the asset-side verification
  // ladder (see ADR-025 amendment), so they're usually identical. Pick
  // the lower of the two to mirror the engine's combMin discipline for
  // multi-input factors when they ever diverge.
  const confidenceTexts = applyingSubs
    .map((f) => f.confidenceText)
    .filter((c): c is string => typeof c === "string");
  const confidenceText = confidenceTexts[0] ?? null;

  const fillClass = factorBarTone(aggPercent);
  const cashExecutionApplies =
    cashExecution !== null && !cashExecution.isUnknown;

  // (Sub-line construction lives in CashSubLine below — it owns the
  // grid alignment, mini-metric ordering, reserves status, and the
  // pending-CE inline fallback.)

  return (
    <div
      data-testid="factor-row"
      data-factor-group="cash_position"
      className="border-b border-panel-ink-foreground/10 py-2 last:border-b-0"
    >
      {/* Top line mirrors singleton FactorRow chrome — label, bar, percent. */}
      <div className="grid grid-cols-[minmax(0,1fr)_minmax(80px,2fr)_auto] items-center gap-4">
        <div className="min-w-0 leading-tight">
          <p
            data-factor-label
            className="truncate text-sm font-semibold text-panel-ink-foreground"
          >
            {CASH_GROUP_LABEL}
          </p>
          <p
            data-factor-confidence
            className="text-[11px] text-panel-ink-foreground/55"
          >
            {confidenceText ?? "— confidence"}
          </p>
        </div>
        <div
          className="relative h-1.5 overflow-hidden rounded-full bg-panel-ink-foreground/14"
          role="meter"
          aria-label="Cash position readiness contribution"
          aria-valuemin={0}
          aria-valuemax={aggMax}
          aria-valuenow={aggValue}
          aria-valuetext={`${aggValue} of ${aggMax} points`}
        >
          <span
            data-readiness-bar-fill=""
            className={cn(
              "absolute inset-y-0 left-0 rounded-full",
              fillClass,
            )}
            style={{ width: `${aggPercent}%` }}
          />
        </div>
        <p
          data-factor-percent
          className="shrink-0 text-lg font-bold tabular-nums leading-none text-panel-ink-foreground"
        >
          {aggPercent}
        </p>
      </div>
      {/* Unified sub-line: reserves status + mini-metrics on one row,
          aligned to the parent's 3-col grid so the mini-bars in col 2
          sit directly under the headline bar above. */}
      <CashSubLine
        downPayment={downPayment}
        cashExecution={cashExecution}
        cashExecutionApplies={cashExecutionApplies}
        planViability={planViability}
      />
    </div>
  );
}

// CashSubLine merges the reserves status, the two sub-factor mini-
// metrics, and the "coming online" fallback onto a single horizontal
// line. Reuses the parent's 3-col grid template so col 2 (mini-bars)
// aligns vertically with col 2 (headline bar) of the row above —
// "structured" rather than free-floating, and the mini-bars read as
// shrunken echoes of the headline.
//
// Layout columns:
//   - col 1: reserves status (⚠ short / ✓ matched), or empty if no
//            preference is set. Aligns under the "Cash position" +
//            confidence stack of the headline row.
//   - col 2: mini-metric blocks separated by middle-dots, with an
//            optional leading dot when reserves occupies col 1 so the
//            line reads as one continuous list.
//   - col 3: empty placeholder so grid math stays clean.
function CashSubLine({
  downPayment,
  cashExecution,
  cashExecutionApplies,
  planViability,
}: {
  downPayment: FactorBreakdown | null;
  cashExecution: FactorBreakdown | null;
  cashExecutionApplies: boolean;
  planViability?: ReadinessPlanViability;
}) {
  const reservesNode = renderReservesInline(planViability);
  const metricNodes: ReactNode[] = [];
  if (downPayment) {
    metricNodes.push(
      <CashSubMetric key="down_payment" id="down_payment" factor={downPayment} />,
    );
  }
  if (cashExecutionApplies && cashExecution) {
    metricNodes.push(
      <CashSubMetric key="cash_execution" id="cash_execution" factor={cashExecution} />,
    );
  } else if (!cashExecutionApplies) {
    // Inline-replace the missing CE mini-metric so the layout pattern
    // stays consistent. Subdued tone signals "this slot will fill in
    // once the cashtoclose adapter is wired" without taking visual
    // weight away from the data that IS available. Stretches to fill
    // its slot so the visual rhythm matches an actual metric.
    metricNodes.push(
      <span
        key="cash_execution_pending"
        data-factor-cash-execution-pending
        className="flex-1 text-[10px] italic text-panel-ink-foreground/45"
      >
        Plan-funding signal coming online
      </span>,
    );
  }

  return (
    <div
      data-factor-sub-line
      // Same 3-col grid as the headline row so the mini-bars in col 2
      // land directly under the headline bar.
      className="mt-1.5 grid grid-cols-[minmax(0,1fr)_minmax(80px,2fr)_auto] items-center gap-4"
    >
      <div className="min-w-0">{reservesNode}</div>
      {/* Col 2: mini-metrics stretch to share the full bar-column
          width — each one flex-1, and inside each one the mini-bar
          is flex-1 too, so the two mini-bars span the same horizontal
          extent as the headline bar above. No punctuation between
          metrics; flex gap does the separating. */}
      <div
        data-factor-sub-breakdown
        className="flex items-center gap-4 text-[11px] leading-tight text-panel-ink-foreground/65"
      >
        {metricNodes}
      </div>
      <div aria-hidden="true" />
    </div>
  );
}

// renderReservesInline returns the reserves status node (now a span,
// not a top-level <p>) so it can sit inside the unified sub-line grid
// without bringing its own line-height. Returns null when no
// preference is set — col 1 then collapses to empty.
function renderReservesInline(
  planViability?: ReadinessPlanViability,
): ReactNode {
  const required = planViability?.reserves_required_dollars ?? 0;
  if (required <= 0) return null;
  const short = planViability?.reserves_short === true;
  const gap = planViability?.reserves_gap_dollars ?? 0;
  if (short && gap > 0) {
    return (
      <span
        data-factor-reserves-status="short"
        className="text-[11px] font-medium leading-tight text-warning"
      >
        ⚠ ${formatThousands(gap)} short of reserves
      </span>
    );
  }
  return (
    <span
      data-factor-reserves-status="matched"
      className="text-[11px] leading-tight text-panel-ink-foreground/45"
    >
      ✓ Reserves matched
    </span>
  );
}

// CashSubMetric is the inline mini-metric used inside the Cash position
// supercell — label · mini-bar · percent. The mini-bar reuses
// factorBarTone so the warning/primary diagnostic encoded by the
// headline bar above carries through to each sub-score. The bar is
// aria-hidden because the segment's textual "Label N" already speaks
// the data to assistive tech; adding role="meter" to three nested
// bars per row would be amplification, not accessibility.
function CashSubMetric({
  id,
  factor,
}: {
  id: "down_payment" | "cash_execution";
  factor: FactorBreakdown;
}) {
  const fillClass = factorBarTone(factor.percent);
  const clampedPercent = clamp(factor.percent, 0, 100);
  return (
    <span
      data-factor-sub-segment={id}
      // flex-1 + min-w-0: each metric claims half of col 2 and stays
      // shrinkable when the column tightens. Inside, label and
      // percent are shrink-0 so they hold their content width while
      // the mini-bar (flex-1) absorbs the remaining space.
      className="flex flex-1 min-w-0 items-center gap-1.5 text-[11px] leading-tight text-panel-ink-foreground/65"
    >
      <span className="shrink-0 text-panel-ink-foreground/75">
        {factor.label}
      </span>
      <span
        data-readiness-mini-bar=""
        aria-hidden="true"
        className="relative h-[3px] flex-1 min-w-0 overflow-hidden rounded-full bg-panel-ink-foreground/14"
      >
        <span
          data-readiness-bar-fill=""
          className={cn("absolute inset-y-0 left-0 rounded-full", fillClass)}
          style={{ width: `${clampedPercent}%` }}
        />
      </span>
      <span className="shrink-0 tabular-nums font-semibold text-panel-ink-foreground/85">
        {factor.percent}
      </span>
    </span>
  );
}

// (Legacy ReservesStatusBadge removed 2026-05-25 — reserves status is
// now rendered inline alongside the mini-metrics via renderReservesInline
// in CashSubLine. The detached "badge below the row" treatment was a
// stepping-stone shape; the unified sub-line is the design ceiling.)

function formatThousands(n: number): string {
  // Locale-agnostic thousands separator — matches the backend's
  // humanCommas helper output (e.g. 18000 → "18,000"). The advisor
  // copy upstream uses the same convention.
  if (n < 0) return `-${formatThousands(-n)}`;
  return n.toLocaleString("en-US");
}

// (FactorSubRow was removed 2026-05-25 — the Cash position supercell
// now collapses the per-sub-factor bars into a single compact text
// line so the row stays visually consistent with the other 8 factors.
// See FactorGroupRow above + the corresponding tests in
// breakdown-tabs.test.tsx.)

function FactorRow({ factor }: { factor: FactorBreakdown }) {
  if (factor.isUnknown) {
    // SILL-323: em-dash row for missing-input factors on DNQ profiles.
    // No bar — drawing a 0% fill would imply the factor scored zero
    // rather than "we don't know yet". Sublabel + aria-label spell out
    // the unknown state for assistive tech.
    return (
      <div
        data-testid="factor-row"
        data-factor-unknown="true"
        className="grid grid-cols-[minmax(0,1fr)_minmax(80px,2fr)_auto] items-center gap-4 border-b border-panel-ink-foreground/10 py-2 last:border-b-0"
      >
        <div className="min-w-0 leading-tight">
          <p
            data-factor-label
            className="truncate text-sm font-semibold text-panel-ink-foreground/70"
          >
            {factor.label}
          </p>
          <p
            data-factor-confidence
            className="text-[11px] text-panel-ink-foreground/55"
          >
            Not yet provided
          </p>
        </div>
        <div aria-hidden="true" />
        <p
          data-factor-percent
          className="shrink-0 text-lg font-bold tabular-nums leading-none text-panel-ink-foreground/50"
          aria-label={`${factor.label} unknown`}
        >
          —
        </p>
      </div>
    );
  }
  const fillClass = factorBarTone(factor.percent);
  const clampedPercent = clamp(factor.percent, 0, 100);

  return (
    <div
      data-testid="factor-row"
      className="grid grid-cols-[minmax(0,1fr)_minmax(80px,2fr)_auto] items-center gap-4 border-b border-panel-ink-foreground/10 py-2 last:border-b-0"
    >
      <div className="min-w-0 leading-tight">
        <p
          data-factor-label
          className="truncate text-sm font-semibold text-panel-ink-foreground"
        >
          {factor.label}
        </p>
        <p
          data-factor-confidence
          className="text-[11px] text-panel-ink-foreground/55"
        >
          {factor.confidenceText ?? "— confidence"}
        </p>
      </div>
      <div
        className="relative h-1.5 overflow-hidden rounded-full bg-panel-ink-foreground/14"
        role="meter"
        aria-label={`${factor.label} readiness contribution`}
        aria-valuemin={0}
        aria-valuemax={factor.max}
        aria-valuenow={factor.value}
        aria-valuetext={`${factor.value} of ${factor.max} points`}
      >
        <span
          data-readiness-bar-fill=""
          className={cn(
            "absolute inset-y-0 left-0 rounded-full",
            fillClass,
          )}
          style={{ width: `${clampedPercent}%` }}
        />
      </div>
      <p
        data-factor-percent
        className="shrink-0 text-lg font-bold tabular-nums leading-none text-panel-ink-foreground"
      >
        {factor.percent}
      </p>
    </div>
  );
}

// -------- Timeline tab --------

function TimelineList({ unlocks }: { unlocks: ReadinessTimeUnlock[] }) {
  return (
    <section data-testid="readiness-time-unlocks" className="flex flex-col gap-4">
      {unlocks.map((unlock) => (
        <TimelineRow
          key={`${unlock.factor}-${unlock.unlock_at}`}
          unlock={unlock}
        />
      ))}
    </section>
  );
}

function TimelineRow({ unlock }: { unlock: ReadinessTimeUnlock }) {
  const months = parseHorizonMonths(unlock.unlock_at);
  const endLabel = endLabelFor(unlock.unlock_at);
  const ticks = buildTimelineTicks(months, endLabel);

  return (
    <article
      data-testid="timeline-row"
      className="grid grid-cols-1 gap-5 rounded-2xl border border-panel-ink-foreground/10 bg-panel-ink-foreground/[0.03] p-5 md:grid-cols-[minmax(260px,1fr)_minmax(0,1.4fr)] md:items-center"
    >
      <div
        data-timeline-narrative
        className="rounded-xl border border-primary/40 bg-primary/5 p-4"
      >
        <div className="flex items-start gap-3">
          <Clock
            className="mt-0.5 h-5 w-5 shrink-0 text-primary"
            aria-hidden="true"
          />
          <div className="min-w-0">
            <h3 className="text-base font-semibold leading-snug text-panel-ink-foreground">
              {unlock.unlock_at}
            </h3>
            <p className="mt-1 text-sm leading-6 text-panel-ink-foreground/70">
              {unlock.why}
            </p>
            {unlock.projected_delta > 0 ? (
              <p className="mt-2 text-sm leading-6 text-panel-ink-foreground/70">
                That's worth roughly{" "}
                <span className="font-semibold text-panel-ink-foreground">
                  +{unlock.projected_delta} points
                </span>{" "}
                without lifting a finger.
              </p>
            ) : null}
          </div>
        </div>
      </div>
      <div data-timeline-strip className="min-w-0 px-1">
        <div className="grid grid-cols-5">
          {ticks.map((tick) => (
            <p
              key={tick.label}
              className={cn(
                "text-xs font-medium",
                tick.atEnd ? "text-right" : "text-left",
                tick.atEnd
                  ? "text-panel-ink-foreground/85"
                  : "text-panel-ink-foreground/55",
              )}
            >
              {tick.label}
            </p>
          ))}
        </div>
        <div className="relative mt-3 h-2">
          <div className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-panel-ink-foreground/20" />
          <div className="absolute left-0 top-1/2 h-px w-[12%] -translate-y-1/2 bg-primary" />
          {ticks.map((tick, idx) => {
            const filled = idx === 0 || idx === ticks.length - 1;
            return (
              <span
                key={`dot-${tick.label}`}
                className={cn(
                  "absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2",
                  filled
                    ? "border-primary bg-primary"
                    : "border-panel-ink-foreground/30 bg-panel-ink",
                )}
                style={{ left: `${tick.positionPct}%` }}
              />
            );
          })}
        </div>
      </div>
    </article>
  );
}

function buildTimelineTicks(
  months: number | null,
  endLabel: string,
): Array<{ label: string; positionPct: number; atEnd: boolean }> {
  // Fixed 5-point scaffold matching the design comp: Today / +N1 / +N2 / +N3 / end.
  const positions = [0, 25, 50, 75, 100];
  if (months === null || months <= 0) {
    return [
      { label: "Today", positionPct: positions[0]!, atEnd: false },
      { label: "+1", positionPct: positions[1]!, atEnd: false },
      { label: "+2", positionPct: positions[2]!, atEnd: false },
      { label: "+3", positionPct: positions[3]!, atEnd: false },
      { label: endLabel, positionPct: positions[4]!, atEnd: true },
    ];
  }
  const step = months / 4;
  const fmt = (n: number) =>
    Number.isInteger(n) ? `+${n} mo` : `+${n.toFixed(1)} mo`;
  return [
    { label: "Today", positionPct: positions[0]!, atEnd: false },
    { label: fmt(step), positionPct: positions[1]!, atEnd: false },
    { label: fmt(step * 2), positionPct: positions[2]!, atEnd: false },
    { label: fmt(step * 3), positionPct: positions[3]!, atEnd: false },
    { label: endLabel, positionPct: positions[4]!, atEnd: true },
  ];
}

function endLabelFor(unlockAt: string): string {
  // Pull the human prefix before " in N months" so the strip's end label
  // reads "Lease ends" rather than "Lease ends in ~12 months".
  const match = unlockAt.match(/^(.*?)\s+in\s+/i);
  if (match && match[1]) return match[1].trim();
  return "Unlock";
}


function buildTimelineMeta(unlocks: ReadinessTimeUnlock[]): string | null {
  if (unlocks.length === 0) return null;
  const horizons = unlocks
    .map((u) => parseHorizonMonths(u.unlock_at))
    .filter((m): m is number => m !== null);
  if (horizons.length === 0) return `${unlocks.length} events`;
  const max = Math.max(...horizons);
  return `~${max} mo`;
}

// -------- Shared helpers --------


// Hybrid tone scale (post-interview): red only when underwater (<45),
// uniform brand-green above that — matches the screenshot comps while
// preserving the at-a-glance "this factor is critical" red flag.
function factorBarTone(percent: number): string {
  if (percent < 75) return "bg-warning";
  return "bg-primary";
}

function goalBarTone(percent: number | null): string {
  if (percent === null) return "bg-primary";
  if (percent < 45) return "bg-destructive";
  return "bg-primary";
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}
